import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Spinner, Typography } from "@/components/atoms";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useDetachClientFromProviderMutation } from "@/redux/apis/account/accountApi";
import { operatorApi, useGetClientOperatorsQuery } from "@/redux/apis/operator/operatorApi";
import { useAppDispatch, useAppResetApi, useAppSelector } from "@/redux/hooks";
import { clearActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { profileSelector } from "@/redux/slices/profile/selectors";
import { subscriptionExpiredSelector } from "@/redux/slices/ui/selectors";
import { setSubscriptionExpired } from "@/redux/slices/ui/uiSlice";
import { addToast } from "@/utils";

/**
 * Blocking modal shown when the connected operator's RideMinder subscription lapses. The baseQuery
 * 403 interceptor flips `ui.subscriptionExpired`, and this overlay pauses the whole client portal
 * until the user either logs out or detaches and joins another operator. Non-dismissable by design:
 * `onClose` is a no-op and there is no close affordance, so booking access can't be resumed against a
 * lapsed operator by clicking the backdrop.
 */
export const SubscriptionExpired = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const resetApi = useAppResetApi();
  const { logout } = useKindeAuth();
  const phpHostUrl = getPhpHostUrl();
  const profile = useAppSelector(profileSelector);
  const activeOperator = useAppSelector(activeOperatorSelector);
  const subscriptionExpired = useAppSelector(subscriptionExpiredSelector);
  const [detachClientFromProvider, { isLoading: isDetaching }] = useDetachClientFromProviderMutation();
  // Detach from the client's actual operator relationship (primary, else first). `activeOperator` is
  // unreliable here: the client-setup flow never sets it, and on the web portal it holds the RideMinder
  // platform/branding provider, not the client's operator. Fetched only while the modal is up.
  const { data: clientOperators } = useGetClientOperatorsQuery(undefined, { skip: !subscriptionExpired });
  const operators = clientOperators?.data.operators ?? [];
  const currentOperator = operators.find((operator) => operator.primary) ?? operators[0] ?? activeOperator;

  const goToJoinOperator = () => {
    dispatch(setSubscriptionExpired(false));
    dispatch(clearActiveOperator());
    // Detach lives in accountApi; the client's operator list is in operatorApi, so cross-invalidate it
    // here so the join screen reflects the just-detached relationship instead of a stale cache.
    dispatch(operatorApi.util.invalidateTags(["Operators"]));
    navigate("/client/join-operator");
  };

  const handleJoinAnotherOperator = () => {
    // Nothing to detach — go straight to the recovery flow.
    if (!currentOperator?.id) {
      goToJoinOperator();
      return;
    }

    detachClientFromProvider({ clientId: profile.id, providerId: currentOperator.id })
      .unwrap()
      .then(goToJoinOperator)
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const handleLogout = async () => {
    dispatch(setSubscriptionExpired(false));
    resetApi();
    if (import.meta.env.MODE === "production") {
      window.location.href = `${phpHostUrl}/logout`;
    } else {
      await logout();
    }
  };

  return (
    <Modal
      open={subscriptionExpired}
      // Non-dismissable: swallow backdrop/escape close attempts.
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClose={() => {}}
      variant="web-small"
      title="Operator Account is Currently Unavailable"
      renderButtons={() => (
        <>
          <Button variant="secondary" size="md" onClick={handleLogout} disabled={isDetaching}>
            Logout
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleJoinAnotherOperator}
            disabled={isDetaching}
            startIcon={isDetaching ? <Spinner className="h-4 w-4" /> : undefined}
          >
            Join Another Operator
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          The operator you&apos;re connected to doesn&apos;t have an active RideMinder subscription right now, so booking access is paused.
        </Typography>
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          To keep using RideMinder, you can connect to another operator account.
        </Typography>
      </div>
    </Modal>
  );
};
